import { DateTime } from "luxon";
import { AgGrid } from "@jsq/ds/core";
import { type GetDistributionBatchesForCanopyPaymentBatchCreationQuery } from "#graphql/supergraphTypes";
import { toDbId } from "#utils/graphql_utils";
import { type ActionType } from "../../common/components/ActionButton";
import { DEFAULT_BASE_MONEY_PROPS } from "../Money/constants";
import { MoneyType } from "../Money/types";
import { getTotalMonies } from "../Money/utils";
import { subtractMoney } from "../Wallet/utils";
import { DistributionBatchRowItem } from "./types";

/**
 * Minimal interface for distribution batch data needed to calculate remaining amount to pay.
 * This allows the utility to work with different GraphQL query result types.
 */
export interface DistributionBatchForCalculation {
  batchTotalDetails?: { amountPayable: { monies: MoneyType[] } } | null;
  amountPaid?: {
    ach?: { monies: MoneyType[] } | null;
    check?: { monies: MoneyType[] } | null;
    manual?: { monies: MoneyType[] } | null;
  } | null;
  amountPaidViaAutomatedPayments?: MoneyType | null;
}

/**
 * Minimal interface for distribution batch data needed to transform into a DistributionBatchRowItem.
 * This allows the utility to work with different GraphQL query result types
 * (e.g., GetDistributionBatchesForCanopyPaymentBatchCreationQuery and GetDistributionBatchForSlideoutQuery).
 */
export interface DistributionBatchForRowItem extends DistributionBatchForCalculation {
  id: string;
  description?: string | null;
  effectiveDate?: string | null;
  distributionCount?: number | null;
  paymentBatches?: Array<{ id: string }> | null;
  arena?: {
    name?: string | null;
    jsqHostname?: string | null;
  } | null;
  entity?: {
    id?: string | null;
    name?: string | null;
  } | null;
}

/**
 * Determines the action type for a distribution batch row based on:
 * - If there are payment batches requiring treasury review -> "review"
 * - Else if remaining to pay > 0 -> "pay"
 * - Otherwise -> null (no action)
 *
 * @param hasPaymentBatchesForReview - Whether there are payment batches needing review
 * @param remainingToPayAmount - The remaining amount to pay
 */
export const determineActionType = (
  hasPaymentBatchesForReview: boolean,
  remainingToPayAmount: MoneyType,
): ActionType => {
  // Payment batches needing review take priority
  if (hasPaymentBatchesForReview) {
    return "review";
  }

  // Check if remaining to pay is greater than 0
  if (remainingToPayAmount.amount > 0) {
    return "pay";
  }

  return null;
};

/**
 * Transforms a distribution batch object into a DistributionBatchRowItem.
 * This utility works with different GraphQL query result types that conform to
 * the DistributionBatchForRowItem interface.
 *
 * @param distributionBatch - Distribution batch data conforming to DistributionBatchForRowItem interface
 */
export const toDistributionBatchRowItem = (
  distributionBatch: DistributionBatchForRowItem,
): DistributionBatchRowItem => {
  const distributionAmount =
    getTotalMonies(distributionBatch.batchTotalDetails?.amountPayable.monies) ||
    DEFAULT_BASE_MONEY_PROPS;

  const remainingToPayAmount = calculateRemainingToPayAmount(distributionBatch);
  // paymentBatches is optional - when provided (filtered by READY_FOR_TREASURY_REVIEW),
  // we use it to determine the action type. When not provided (e.g., in DistributionSlideout),
  // the action is determined separately by the PaymentBatchesTable component.
  const hasPaymentBatchesForReview = (distributionBatch.paymentBatches?.length ?? 0) > 0;
  const action = determineActionType(hasPaymentBatchesForReview, remainingToPayAmount);

  // Extract payment batch IDs for review routing
  const paymentBatchesForReview = (distributionBatch.paymentBatches || []).map((pb) => pb.id);

  return {
    id: toDbId(distributionBatch.id),
    description: distributionBatch.description,
    distributionDate: DateTime.fromISO(distributionBatch.effectiveDate as string),
    clientName: distributionBatch.arena?.name || "",
    jsqHostName: distributionBatch.arena?.jsqHostname || "",
    entityId: distributionBatch.entity?.id ? toDbId(distributionBatch.entity?.id) : null,
    entityName: distributionBatch.entity?.name || "",
    positionsCount: distributionBatch.distributionCount ?? 0,
    distributionAmount,
    remainingToPayAmount,
    action,
    paymentBatchesForReview,
  };
};

/**
 * This utility function takes in a DistributionBatches query result and
 * transforms each distribution batch into a row object that is passed into AGGrid.
 *
 * It also returns a rowCount property that tells AGGrid of the total number of rows
 * so it knows when to stop paginating.
 *
 * @param data - The distribution batches query result
 */
export const toLoadSuccessParams = (
  data: GetDistributionBatchesForCanopyPaymentBatchCreationQuery,
): { resp: AgGrid.LoadSuccessParams } => {
  const rowData: DistributionBatchRowItem[] =
    data.distributionBatches?.edges?.map((edge) => {
      const distributionBatch = edge?.node;
      const distributionAmount =
        getTotalMonies(distributionBatch.batchTotalDetails?.amountPayable.monies) ||
        DEFAULT_BASE_MONEY_PROPS;
      const remainingToPayAmount = calculateRemainingToPayAmount(distributionBatch);

      // Check if there are payment batches requiring treasury review
      // The query filters for READY_FOR_TREASURY_REVIEW status, so any results mean review is needed
      const hasPaymentBatchesForReview = (distributionBatch.paymentBatches?.length ?? 0) > 0;

      // Extract payment batch IDs for review routing
      const paymentBatchesForReview = (distributionBatch.paymentBatches || []).map(
        (pb) => pb.id,
      );

      return {
        id: toDbId(distributionBatch.id),
        description: distributionBatch.description,
        distributionDate: DateTime.fromISO(distributionBatch.effectiveDate as string),
        clientName: distributionBatch.arena?.name || "",
        jsqHostName: distributionBatch.arena?.jsqHostname || "",
        entityId: distributionBatch.entity?.id ? toDbId(distributionBatch.entity?.id) : null,
        entityName: distributionBatch.entity?.name || "",
        positionsCount: distributionBatch.distributionCount!,
        distributionAmount,
        remainingToPayAmount,
        action: determineActionType(hasPaymentBatchesForReview, remainingToPayAmount),
        paymentBatchesForReview,
      };
    }) || [];
  return {
    resp: {
      rowData: rowData || [],
      rowCount: data.distributionBatches?.count || 0,
    },
  };
};

/**
 * This utility function calculates the remaining amount to pay for a distribution batch.
 * It subtracts the amount paid via ACH, check, manual, and automated payments
 * from the total amount payable.
 *
 * @param distributionBatch - Distribution batch data conforming to DistributionBatchForCalculation interface
 */
export const calculateRemainingToPayAmount = (
  distributionBatch: DistributionBatchForCalculation,
): MoneyType => {
  let remainingToPayAmount = distributionBatch.batchTotalDetails?.amountPayable.monies || [];
  const amountPaid = distributionBatch.amountPaid;
  if (amountPaid?.ach) {
    remainingToPayAmount = subtractMoney(remainingToPayAmount, amountPaid.ach.monies);
  }
  if (amountPaid?.check) {
    remainingToPayAmount = subtractMoney(remainingToPayAmount, amountPaid.check.monies);
  }
  if (amountPaid?.manual) {
    remainingToPayAmount = subtractMoney(remainingToPayAmount, amountPaid.manual.monies);
  }

  if (distributionBatch.amountPaidViaAutomatedPayments) {
    remainingToPayAmount = subtractMoney(remainingToPayAmount, [
      distributionBatch.amountPaidViaAutomatedPayments,
    ]);
  }

  return getTotalMonies(remainingToPayAmount) || DEFAULT_BASE_MONEY_PROPS;
};
